import { ClipboardCheck, Hand, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { NumberInput } from '@/components/ui/number-input';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { uploadRulePhoto } from './driver-rules-api';
import { PhotoField } from './PhotoField';
import { myTaskCounts, taskDueText, useMyTasks, useTaskAction, type FleetTask } from './task-api';

/**
 * DEC-430: thẻ "Việc được giao" trên màn Tài xế – bảo dưỡng tới hạn, rửa xe, giấy tờ cần cập nhật, nhắc việc khẩn của quản lý.
 * Thành phần độc lập: màn Tài xế chỉ cần gắn <DriverTasksCard />; không có việc thì không hiện gì.
 * Nút lớn: Nhận việc → Xong (chụp ảnh, nhập km khi bảo dưỡng). Việc báo xong chờ quản lý xác nhận hiện mờ ở cuối.
 * Anh Tiến 01/11/2026: thẻ chỉ mở từ nút "Việc được giao" trên cùng đầu trang (/tai-xe?viec=1), không chồng lên màn Giao hàng; showEmpty = trống vẫn báo "Không có việc".
 */
export function DriverTasksCard({ showEmpty = false }: { showEmpty?: boolean }) {
  const q = useMyTasks();
  const items = q.data?.items ?? [];
  const todo = items.filter((t) => t.status === 'open' || t.status === 'accepted');
  if (!items.length) {
    if (!showEmpty || q.isLoading) return null;
    return (
      <p className="rounded-xl border bg-card p-4 text-center text-base text-muted-foreground" data-testid="driver-tasks-empty">
        Không có việc được giao. Có việc mới chuông và điện thoại sẽ báo.
      </p>
    );
  }
  const urgent = todo.some((t) => t.priority === 'urgent' || t.overdue);
  return (
    <section className={cn('rounded-xl border-2 bg-card', urgent ? 'border-destructive' : todo.length ? 'border-warning' : 'border-border')} aria-label="Việc được giao" data-testid="driver-tasks">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <ClipboardCheck className={cn('size-5', urgent ? 'text-destructive' : 'text-primary')} />
        <h2 className="font-semibold">Việc được giao</h2>
        {todo.length > 0 && <Badge variant={urgent ? 'error' : 'warning'}>{todo.length} việc</Badge>}
      </div>
      <ul className="divide-y">
        {items.map((t) => (
          <TaskRow key={t.id} t={t} />
        ))}
      </ul>
    </section>
  );
}

function TaskRow({ t }: { t: FleetTask }) {
  const [doing, setDoing] = useState(false);
  const accept = useTaskAction(() => api.post(`/fleet/tasks/${t.id}/accept`, { version: t.version }), 'Đã nhận việc');
  const finished = t.status === 'done' || t.status === 'closed';
  return (
    <li className={cn('space-y-2 px-3 py-2.5', finished && 'opacity-70')} data-testid="driver-task">
      <div className="flex flex-wrap items-center gap-1.5">
        {t.priority === 'urgent' && !finished && <Badge variant="error">Khẩn</Badge>}
        {t.overdue && <Badge variant="error">Quá hạn</Badge>}
        <Badge variant="outline">{t.sourceLabel}</Badge>
        {finished && <Badge variant={t.status === 'closed' ? 'success' : 'info'}>{t.status === 'closed' ? 'Đã xong' : 'Chờ quản lý xác nhận'}</Badge>}
        {taskDueText(t) && !finished && <span className={cn('ml-auto text-xs', t.overdue ? 'font-semibold text-destructive' : 'text-muted-foreground')}>{taskDueText(t)}</span>}
      </div>
      <p className="text-base font-semibold leading-snug">
        {t.plateNo && <span className="text-primary">{t.plateNo} · </span>}
        {t.title}
      </p>
      {t.detail && <p className="text-sm text-muted-foreground">{t.detail}</p>}
      {t.status === 'accepted' && t.closeNote && <p className="rounded bg-warning-soft px-2 py-1 text-sm text-warning-foreground">Quản lý trả lại: {t.closeNote}</p>}
      {t.createdByName && !finished && <p className="text-xs text-muted-foreground">Giao bởi {t.createdByName}</p>}
      {!finished && !doing && (
        <div className="grid grid-cols-2 gap-2">
          {t.status === 'open' ? (
            <Button size="lg" className="h-12 text-base" disabled={accept.isPending} onClick={() => accept.mutate(undefined)} data-testid="task-accept">
              <Hand /> Nhận việc
            </Button>
          ) : (
            <span className="flex items-center justify-center rounded-md bg-info-soft text-sm text-info-foreground">Đang làm</span>
          )}
          <Button size="lg" variant={t.status === 'accepted' ? 'default' : 'outline'} className="h-12 text-base" onClick={() => setDoing(true)} data-testid="task-done">
            <ClipboardCheck /> Xong
          </Button>
        </div>
      )}
      {doing && <DoneForm t={t} onClose={() => setDoing(false)} />}
    </li>
  );
}

function DoneForm({ t, onClose }: { t: FleetTask; onClose: () => void }) {
  const [photoId, setPhotoId] = useState<number | null>(null);
  const [km, setKm] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const done = useTaskAction(
    () => api.post(`/fleet/tasks/${t.id}/done`, { version: t.version, photoId, odometerKm: km, note: note || null }),
    t.isWash ? 'Đã ghi rửa xe, 7 ngày sau nhắc lại' : 'Đã báo xong, chờ quản lý xác nhận',
    onClose,
  );
  const photoLabel = t.isWash ? 'Ảnh xe đã rửa' : t.requireKm ? 'Ảnh đồng hồ km' : t.source === 'manual' ? 'Ảnh việc đã làm' : 'Ảnh giấy tờ mới';
  return (
    <div className="space-y-2 rounded-lg border bg-muted/30 p-2.5">
      <PhotoField label={photoLabel} kind="other" upload={(f) => uploadRulePhoto('task', f)} value={photoId} onChange={setPhotoId} required={t.requirePhoto} />
      {t.requireKm && (
        <div>
          <Label>
            Km đồng hồ lúc làm <span className="text-destructive">*</span>
          </Label>
          <NumberInput className="h-11 w-full rounded-md border px-2 text-base" decimals={1} value={km} onValueChange={setKm} aria-label="Km đồng hồ" />
        </div>
      )}
      <Input value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Ghi chú (gara, chi phí, vướng gì…)" aria-label="Ghi chú" />
      <div className="grid grid-cols-2 gap-2">
        <Button variant="ghost" onClick={onClose}>
          Thôi
        </Button>
        <Button
          size="lg"
          className="h-12 text-base"
          disabled={done.isPending || (t.requirePhoto && !photoId) || (t.requireKm && km === null)}
          onClick={() => done.mutate(undefined)}
          data-testid="task-send-done"
        >
          Gửi báo xong
        </Button>
      </div>
    </div>
  );
}

/** Nhãn đỏ "Gấp N" (việc Khẩn / Quá hạn) hoặc số vàng (việc thường), dùng ở tab Việc giao và nút đầu trang. */
export function TaskGapBadge({ gap, todo, compact, className }: { gap: number; todo: number; compact?: boolean; className?: string }) {
  if (!todo) return null;
  return gap > 0 ? (
    <span className={cn('inline-flex items-center gap-0.5 rounded-full bg-destructive px-1.5 text-xs font-bold leading-5 text-white ring-2 ring-card', className)} data-testid="task-gap-badge">
      {!compact && <TriangleAlert className="size-3" />} Gấp {gap > 99 ? '99+' : gap}
    </span>
  ) : (
    <span className={cn('min-w-5 rounded-full bg-warning px-1.5 text-center text-xs font-bold leading-5 text-warning-foreground ring-2 ring-card', className)} data-testid="task-todo-badge">
      {todo > 99 ? '99+' : todo}
    </span>
  );
}

/**
 * Anh Tiến 01/11/2026 ("chỉ để trên cùng thôi, nhấn vào và xem", "để rõ trên này, chiếm trọn chỗ đó"): nút "Việc được giao" trên cùng đầu trang của tài xế
 * (cạnh Bảng tin, Hướng dẫn, chuông), bấm mở màn Việc được giao. Nhãn đỏ "Gấp N" khi có việc Khẩn / Quá hạn, số vàng khi chỉ có việc thường.
 */
export function DriverTasksHeaderButton() {
  const q = useMyTasks();
  const { todo, gap } = myTaskCounts(q.data?.items ?? []);
  return (
    <Link
      to="/tai-xe?viec=1"
      className={cn(
        'flex h-11 w-full min-w-0 items-center gap-1.5 rounded-xl border-2 px-2 text-sm font-bold leading-tight hover:bg-muted',
        gap ? 'border-destructive bg-error-soft text-destructive' : todo ? 'border-warning text-foreground' : 'text-muted-foreground',
      )}
      title={todo ? `${todo} việc được giao${gap ? `, ${gap} gấp` : ''}` : 'Việc được giao'}
      aria-label={todo ? `Việc được giao: ${todo} việc${gap ? `, ${gap} gấp` : ''}` : 'Việc được giao'}
      data-testid="header-driver-tasks"
    >
      {/* Điện thoại hẹp: bỏ hình để chữ "Việc giao" và nhãn Gấp đủ chỗ */}
      <ClipboardCheck className="size-5 shrink-0 max-[420px]:hidden" />
      <span className="min-w-0 flex-1 whitespace-nowrap">Việc giao</span>
      <TaskGapBadge gap={gap} todo={todo} compact className="shrink-0 whitespace-nowrap px-1.5 text-sm leading-6 ring-0" />
    </Link>
  );
}
